import {
  Box,
  Button,
  Container,
  Grid,
  Group,
  NativeSelect,
  NumberInput,
  Select,
  Text,
  TextInput,
  Textarea,
  Title,
  createStyles,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { useClipboard } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';
import {
  CircleCheck,
  Copy,
  DeviceFloppy,
  Refresh,
  X,
} from 'tabler-icons-react';
import { v4 as uuidv4 } from 'uuid';
import { Currencies } from '../../enums/currencies.enum';
import { FeeType } from '../../enums/fee-type.enum';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import { GetEnumKeyByEnumValue } from '../../helpers/payment-link.helper';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ProductSchema } from '../../schemas/payment-link.schema';
import { CreateCheckoutRequest } from '../../type/create-checkout-request.type';
import { ErrorResponse } from '../../type/error-response.types';
import {
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import AlternativeUserFilter from '../filters/alternative-merchant-management-filter.component';
import BranchesPaymentLinkFilter from '../filters/branches-payment-link-filter.component';
import MerchantPaymentLinkFilter from '../filters/merchant-pamynet-link-filter.component';
import PartnerPaymentLinkFilter from '../filters/partner-payment-link-filter.component';
import ResellerPaymentLinkFilter from '../filters/reseller-payment-link-filter.component';
import { MdrFee } from '../../type/onboard-merchant-request.types';

const PaymentLinks = () => {
  const [mdrFees, setMdrFees] = useState<MdrFee[]>();
  const [paymentLink, setPaymentLink] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const paymentLinkClipboard = useClipboard();
  const axiosPrivate = useAxiosPrivate();
  const [isLoadPartners, setIsLoadPartners] = useState<boolean>(true);
  const filterSelectData = useForm<UserManagementFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
      merchantBranchesData: [],
    },
  });
  const { auth } = useAuth();

  const createPaymentLinkForm = useForm({
    schema: joiResolver(ProductSchema),
    initialValues: {
      name: '',
      code: '',
      description: '',
      price: 0,
      quantity: 1,
      currency: GetEnumKeyByEnumValue(Currencies, Currencies.PHP),
      cardFee: 0,
      qrFee: 0,
    },
  });

  enum FeeTypeDisplay {
    PERCENTAGE = '%',
    FLAT = 'PHP',
  }

  const [cardFeeTypeDisplay, setCardFeeTypeDisplay] = useState<FeeTypeDisplay>(
    FeeTypeDisplay.PERCENTAGE
  );
  const [qrFeeTypeDisplay, setQrFeeTypeDisplay] = useState<FeeTypeDisplay>(
    FeeTypeDisplay.PERCENTAGE
  );

  const createPaymentLink = async (
    values: typeof createPaymentLinkForm.values
  ) => {
    const { pos, partner, reseller, merchant, merchantBranch } =
      filterValues.values;

    if (!pos) {
      return;
    }

    const reqRefNo = uuidv4();

    const totalAmount = Number(values.price) * values.quantity;
    let cardFee = values.cardFee;
    let qrFee = values.qrFee;
    let cardFeeType = FeeType.FLAT;
    let qrFeeType = FeeType.FLAT;

    if (cardFeeTypeDisplay === FeeTypeDisplay.FLAT) {
      cardFee = (values.cardFee / totalAmount) * 100;
    } else if (cardFeeTypeDisplay === FeeTypeDisplay.PERCENTAGE) {
      cardFeeType = FeeType.PERCENTAGE;
    }

    if (qrFeeTypeDisplay === FeeTypeDisplay.FLAT) {
      qrFee = (values.qrFee / totalAmount) * 100;
    } else if (qrFeeTypeDisplay === FeeTypeDisplay.PERCENTAGE) {
      qrFeeType = FeeType.PERCENTAGE;
    }

    const reqPayload: CreateCheckoutRequest = {
      posId: pos,
      totalAmount: {
        currency: values.currency!,
        value: totalAmount.toString(),
      },
      charges: {
        card: {
          fee: cardFee / 100,
          feeType: cardFeeType,
        },
        qr: {
          fee: qrFee / 100,
          feeType: qrFeeType,
        },
      },
      buyer: {
        firstName: 'Gilbert',
        lastName: 'Flores',
        contact: {
          phone: '09279850413',
          email: 'gilbert.flores@gmail.com',
        },
        shippingAddress: {
          line1: 'FAFFI WAREHOUSE, 2A DR NATIVIDAD, Ibayo-Tipas',
          city: 'Taguig',
          state: 'Metro Manila',
          postalCode: '1632',
          alphaCountryCode: 'PHL',
        },
      },
      items: [
        {
          name: values.name,
          code: values.code,
          description: values.description,
          quantity: values.quantity.toString(),
          amount: {
            value: values.price.toString(),
          },
        },
      ],
      redirectUrls: {
        success: `${process.env.REACT_APP_PAYMENT_LINK_REDIRECT_URL_DOMAIN}/transaction/success?refNo=${reqRefNo}`,
        failure: `${process.env.REACT_APP_PAYMENT_LINK_REDIRECT_URL_DOMAIN}/transaction/failed?refNo=${reqRefNo}`,
      },
      requestReferenceNumber: reqRefNo,
    };

    if (partner) {
      reqPayload.partnerId = partner;
    }
    if (reseller) {
      reqPayload.resellerId = reseller;
    }
    if (merchant) {
      reqPayload.merchantId = merchant;
    }
    if (merchantBranch) {
      reqPayload.branchId = merchantBranch;
    }

    try {
      setIsLoading(true);
      const response = await axiosPrivate.post(
        `/web/payment_links`,
        reqPayload
      );
      const { url } = response.data;
      setPaymentLink(url);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-payment-link',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        color: 'red',
        icon: <X size={14} />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const convertFeeType = (
    feeType: FeeTypeDisplay,
    fee: number,
    totalAmount: number
  ): number => {
    let computedFee: number = 0;

    if (feeType === FeeTypeDisplay.FLAT) {
      computedFee = totalAmount * (fee / 100);
    } else if (feeType === FeeTypeDisplay.PERCENTAGE) {
      computedFee = (fee / totalAmount) * 100 || 0; // Add || 0 because of possible NaN
    }

    return computedFee;
  };

  const filterValues = useForm<UserManagementFilterForm>({
    initialValues: {
      partner: '',
      reseller: '',
      merchant: '',
      merchantBranch: '',
    },
  });

  const getNextFilterStep = () => {
    let nextStep = '';

    if (!filterValues.values.partner && auth?.role === Roles.ADMINISTRATOR) {
      nextStep = 'Select Partner';
    } else if (
      !filterValues.values.reseller &&
      (auth?.role === Roles.PARTNER || auth?.role === Roles.ADMINISTRATOR)
    ) {
      nextStep = 'Select Reseller';
    } else if (
      !filterValues.values.merchant &&
      (auth?.role === Roles.PARTNER ||
        auth?.role === Roles.ADMINISTRATOR ||
        auth?.role === Roles.RESELLER)
    ) {
      nextStep = 'Select Merchant';
    } else if (
      !filterValues.values.merchantBranch &&
      (auth?.role === Roles.PARTNER ||
        auth?.role === Roles.ADMINISTRATOR ||
        auth?.role === Roles.RESELLER ||
        auth?.role === Roles.MERCHANT)
    ) {
      nextStep = 'Select Branch';
    } else if (!filterValues.values.pos) {
      nextStep = 'Select POS';
    } else {
      nextStep = 'Proceed to Step 2';
    }

    return <>{'1. ' + nextStep}</>;
  };

  const isRoleAdministrator = auth?.role === Roles.ADMINISTRATOR;
  const isRolePartner = auth?.role === Roles.PARTNER;
  const isRoleReseller = auth?.role === Roles.RESELLER;
  const isRoleMerchant = auth?.role === Roles.MERCHANT;

  return (
    <Box>
      <Title order={2} my="lg">
        Create a Payment Link
      </Title>

      <Text mb="xl" sx={{ width: '70%' }} size="sm">
        With Payment Links, you can use XPay&apos;s payment page to sell a
        product, and share a link to it with your customers. You can share the
        link as many times as you want on social media, in emails, or any other
        channel.
      </Text>

      <Text weight="bold">{getNextFilterStep()}</Text>

      {isRoleAdministrator && (
        <Container fluid p={0} m={0} pt="md" mb="xl">
          <AlternativeUserFilter
            filterValues={filterValues}
            setIsLoadPartners={setIsLoadPartners}
            isLoadPartners={isLoadPartners}
            filterSelectData={filterSelectData}
            mode={UserManagementFilterMode.PAYMENT_LINK_MODE}
            mdrFees={mdrFees}
            setMdrFees={setMdrFees}
          />
        </Container>
      )}

      {isRolePartner && (
        <Container fluid p={0} m={0} pt="md" mb="xl">
          <PartnerPaymentLinkFilter
            filterValues={filterValues}
            setIsLoadResellers={setIsLoadPartners}
            isLoadResellers={isLoadPartners}
            filterSelectData={filterSelectData}
            mode={UserManagementFilterMode.PAYMENT_LINK_MODE}
          />
        </Container>
      )}

      {isRoleReseller && (
        <Container fluid p={0} m={0} pt="md" mb="xl">
          <ResellerPaymentLinkFilter
            filterValues={filterValues}
            setIsLoadMerchants={setIsLoadPartners}
            isLoadMerchants={isLoadPartners}
            filterSelectData={filterSelectData}
            mode={UserManagementFilterMode.PAYMENT_LINK_MODE}
          />
        </Container>
      )}

      {isRoleMerchant && (
        <Container fluid p={0} m={0} pt="md" mb="xl">
          <MerchantPaymentLinkFilter
            filterValues={filterValues}
            setIsLoadBranches={setIsLoadPartners}
            isLoadBranches={isLoadPartners}
            filterSelectData={filterSelectData}
            mode={UserManagementFilterMode.PAYMENT_LINK_MODE}
          />
        </Container>
      )}

      {!isRoleAdministrator &&
        !isRoleMerchant &&
        !isRolePartner &&
        !isRoleReseller && (
          <Container fluid p={0} m={0} pt="md" mb="xl">
            <BranchesPaymentLinkFilter
              filterValues={filterValues}
              setIsLoadPOS={setIsLoadPartners}
              isLoadPOS={isLoadPartners}
              filterSelectData={filterSelectData}
            />
          </Container>
        )}

      <Text weight="bold">2. Provide product information</Text>

      <form
        onSubmit={createPaymentLinkForm.onSubmit(createPaymentLink)}
        noValidate
      >
        <Grid grow pt="md">
          <Grid.Col sm={12} md={6}>
            <TextInput
              label="Product Name"
              placeholder="Product Name"
              {...createPaymentLinkForm.getInputProps('name')}
              maxLength={1000}
              required
            />
          </Grid.Col>
          <Grid.Col sm={12} md={6}>
            <TextInput
              label="Product Code"
              placeholder="Product Code"
              {...createPaymentLinkForm.getInputProps('code')}
              maxLength={100}
              required
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Textarea
              placeholder="Describe your product"
              label="Product Description"
              {...createPaymentLinkForm.getInputProps('description')}
              maxLength={120}
              minRows={4}
              required
            />
          </Grid.Col>

          <Grid.Col sm={12} md={6}>
            <TextInput
              type="number"
              placeholder="0"
              {...createPaymentLinkForm.getInputProps('price')}
              label="Price"
              rightSection={
                <NativeSelect
                  {...createPaymentLinkForm.getInputProps('currency')}
                  data={[
                    {
                      value: Currencies.PHP,
                      label: 'PHP',
                    },
                    {
                      value: Currencies.USD,
                      label: 'USD',
                    },
                  ]}
                  styles={{
                    input: {
                      fontWeight: 500,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                  }}
                />
              }
              rightSectionWidth={74}
              required
            />
          </Grid.Col>

          <Grid.Col sm={12} md={6}>
            <NumberInput
              label="Quantity"
              placeholder="1"
              {...createPaymentLinkForm.getInputProps('quantity')}
              min={0}
              required
            />
          </Grid.Col>
        </Grid>

        <Grid pt="lg">
          <Grid.Col xs={6} sm={3}>
            <NumberInput
              label={'Add on Fee for Credit card'}
              {...createPaymentLinkForm.getInputProps('cardFee')}
              required
              hideControls
              precision={2}
              p={0}
              radius={0}
              rightSectionWidth={80}
              rightSection={
                <Select
                  value={cardFeeTypeDisplay}
                  onChange={(value) => {
                    setCardFeeTypeDisplay(value as FeeTypeDisplay);

                    const values = createPaymentLinkForm.values;
                    const totalAmount = values.price * values.quantity;

                    const fee = convertFeeType(
                      value as FeeTypeDisplay,
                      values.cardFee,
                      totalAmount
                    );

                    createPaymentLinkForm.setFieldValue('cardFee', fee);
                  }}
                  data={Object.values(FeeTypeDisplay).map((item) => ({
                    label: item,
                    value: item,
                  }))}
                />
              }
            />
          </Grid.Col>

          <Grid.Col xs={6} sm={3}>
            <NumberInput
              label="Add on Fee for QR"
              {...createPaymentLinkForm.getInputProps('qrFee')}
              required
              hideControls
              precision={2}
              p={0}
              radius={0}
              rightSectionWidth={80}
              rightSection={
                <Select
                  value={qrFeeTypeDisplay}
                  onChange={(value) => {
                    setQrFeeTypeDisplay(value as FeeTypeDisplay);

                    const values = createPaymentLinkForm.values;
                    const totalAmount = values.price * values.quantity;

                    const fee = convertFeeType(
                      value as FeeTypeDisplay,
                      values.qrFee,
                      totalAmount
                    );

                    createPaymentLinkForm.setFieldValue('qrFee', fee);
                  }}
                  data={Object.values(FeeTypeDisplay).map((item) => ({
                    label: item,
                    value: item,
                  }))}
                />
              }
            />
          </Grid.Col>
        </Grid>

        {(auth?.role === Roles.ADMINISTRATOR ||
          auth?.role === Roles.PARTNER ||
          auth?.role === Roles.RESELLER ||
          auth?.role === Roles.MERCHANT ||
          auth?.role === Roles.MERCHANT_BRANCH) && (
          <Grid grow>
            <Grid.Col span={12} mt={30}>
              <Group position="right">
                <Button
                  leftIcon={<Refresh size={16} />}
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    createPaymentLinkForm.reset();

                    setPaymentLink(undefined);
                  }}
                >
                  Clear Form
                </Button>
                <Button
                  type="submit"
                  leftIcon={<DeviceFloppy size={16} />}
                  disabled={isLoading || !filterValues.values.pos}
                  loading={isLoading}
                >
                  Create Payment Link
                </Button>
              </Group>
            </Grid.Col>
          </Grid>
        )}

        {paymentLink && (
          <TextInput
            type="text"
            variant="filled"
            size="md"
            value={paymentLink}
            readOnly
            rightSection={
              <Button
                size="md"
                color={paymentLinkClipboard.copied ? 'green' : 'gray'}
                rightIcon={
                  paymentLinkClipboard.copied ? (
                    <CircleCheck size={20} />
                  ) : (
                    <Copy size={20} />
                  )
                }
                onClick={() => paymentLinkClipboard.copy(paymentLink)}
              >
                {paymentLinkClipboard.copied ? 'Copied' : 'Click to copy'}
              </Button>
            }
            rightSectionWidth={116}
            mt="md"
          />
        )}
      </form>
    </Box>
  );
};

export default PaymentLinks;

const useStyles = createStyles(() => {
  return {
    convenienceFee: {
      whiteSpace: 'nowrap',
    },
    tableContainer: {
      whiteSpace: 'nowrap',
      overflowX: 'auto',
      overflowY: 'hidden',
    },
  };
});
